import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";

import { COUNTRY, IS_BROKER_PORTAL } from "config";
import {
  addOrRemoveQuote,
  addOrRemoveQuoteForSendingEmail,
} from "store/features/quotesSlice";
import {
  clientSelector,
  customStylesSelector,
  quotesSelector,
} from "store/selectors/selectors";
import { RootState } from "store/store";
import { useT } from "translation";
import { CurrencyPrefix, formatCurrency } from "utils/utils";
import { InsurerLogoMapping } from "../utils";
import Coverages from "./Coverages";
import QuotesMissing from "./QuotesMissing";

const PlanPriceCard: React.FC<{
  plan: Quote;
}> = ({ plan }) => {
  const { lang } = useT();
  const dispatch = useDispatch();
  const { t } = useT();

  const customStyles = useSelector(customStylesSelector);
  const {
    selectedQuotes,
    sentQuotesToken,
    selectedQuotesForSendingEmail,
    availableQuotes,
  } = useSelector(quotesSelector);
  const { externalAuthConfig } = useSelector(clientSelector);

  const isAddOn = useSelector((state: RootState) => state.quotes.is_add_on);

  const productIdentifier = useSelector(
    (state: RootState) =>
      state.client.availableProducts[0]?.product?.product_identifier
  );

  const [hasSpan, setHasSpan] = useState(false);
  const [isSelected, setIsSelected] = useState(
    selectedQuotes.find((sq) => sq.id === plan.id)
  );
  const carrier = useSelector(
    (state: RootState) => state.client.availableProducts[0]?.carrier?.[0]
  );
  const isSilverySky =
    COUNTRY === "sg" && productIdentifier === "PET" && carrier === "MSIG";
  const selected = selectedQuotes.find((sq) => sq.id === plan.id);
  const selectedForSendingEmail =
    selectedQuotesForSendingEmail.find((sq) => sq.id === plan.id) ||
    plan?.status === "shortlisted" ||
    plan?.status === "bound";

  const calculatePremium = (plan: Quote, is_add_on: boolean) => {
    if (isSilverySky) {
      if (plan.premium?.term === "year" && plan.premium?.monthly) {
        return formatCurrency(plan.premium.monthly);
      }

      const premiumValue = plan.premium?.premium_value || 0;
      const tax = is_add_on
        ? plan.premium?.tax + plan.data?.addOnTax
        : plan.premium?.tax || 0;
      const addOnValue = plan.data?.addOnPremium || 0;

      const totalPremium = is_add_on
        ? premiumValue + tax + addOnValue
        : premiumValue + tax;

      return formatCurrency(totalPremium);
    }
  };

  useEffect(() => {
    setIsSelected(selected);
  }, [selectedQuotes]);

  useEffect(() => {
    if (
      availableQuotes?.length === 1 &&
      (IS_BROKER_PORTAL || externalAuthConfig?.partner === 'fairfax') &&
      !sentQuotesToken &&
      selectedQuotesForSendingEmail.length === 0
    ) {
      dispatch(addOrRemoveQuoteForSendingEmail(plan));
    }
  }, [selectedQuotes]);

  return (
    <Box sx={{ overflow: "visible", position: "relative" }}>
      {(IS_BROKER_PORTAL || externalAuthConfig?.partner === 'fairfax') && (
        <Box
          data-cy={`${plan?.product_details?.identifier?.toLowerCase()}-${plan?.premium?.additional_info?.plan_name?.default
            .replaceAll(" ", "-")
            .toLowerCase()}`}
          sx={{
            position: "absolute",
            zIndex: 1,
            top: "-8px",
            right: "-8px",
            cursor: "pointer",
          }}
          onClick={() =>
            !sentQuotesToken && dispatch(addOrRemoveQuoteForSendingEmail(plan))
          }
        >
          <CheckCircleIcon
            sx={{
              transition: "0.3s",
              background: "white",
              borderRadius: "12px",
              color: selectedForSendingEmail
                ? "#4CAF50"
                : "rgba(0, 0, 0, 0.54)",
            }}
          />
        </Box>
      )}
      <Box
        data-cy={`${plan.product_details.type}-plan-card`}
        className={`priceCard ${isSelected?.id ? "plan-selected" : ""}`}
        onClick={() => {
          dispatch(addOrRemoveQuote(plan));
          if (!selected) {
            setHasSpan(true);
          }
          setTimeout(() => {
            setHasSpan(false);
          }, 1000);
        }}
        sx={{
          border: "2px solid transparent",
          borderRadius: "12px",
          overflow: "hidden",
          display: "flex",
          minWidth: "60px",
          flexDirection: "column",
          gap: "10px",
          alignItems: "center",
          px: "30px",
          py: "10px",
          cursor: "pointer",
          transition: "0.3s",
          backgroundColor: customStyles.secondaryCard.backgroundColor,
          borderColor: isSelected
            ? customStyles.secondaryCard.active.borderColor
            : "transparent",
          "&:hover": {
            borderColor: isSelected
              ? customStyles.secondaryCard.active.borderColor
              : customStyles.secondaryCard.hover.borderColor,
          },
        }}
      >
        {hasSpan && <span className="ripple"></span>}
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              color: customStyles.muiTheme.palette.text.tertiary,
              textAlign: "center",
            }}
          >
            {plan?.premium?.additional_info?.plan_name?.[lang] ??
              plan?.premium?.additional_info?.plan_name?.default}
            {isAddOn && isSilverySky && " Plan with Non-Surgical Cover"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                mb: "5px",
                fontWeight: 700,
                color: customStyles.muiTheme.palette.text.secondary,
              }}
            >
              {CurrencyPrefix}
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: customStyles.muiTheme.palette.text.secondary,
              }}
            >
              {isSilverySky
                ? calculatePremium(plan, isAddOn)
                : plan.premium?.term === "year" && plan.premium?.monthly
                ? formatCurrency(plan.premium?.monthly)
                : formatCurrency(plan.premium?.premium_value)}
            </Typography>
            {isSilverySky && (
              <Typography
                sx={{
                  fontSize: "12px",
                  mb: "5px",
                  color: customStyles.muiTheme.palette.text.secondary,
                }}
              >
                /{plan.premium.term}
              </Typography>
            )}
          </Box>
          {isSilverySky && (
            <Typography sx={{ fontSize: "11px" }}>Inclusive of GST</Typography>
          )}
          {plan.premium?.term === "year" && plan.premium?.monthly && (
            <Typography
              sx={{
                fontSize: "14px",
                mt: "5px",
                fontWeight: 700,
                color: customStyles.muiTheme.palette.text.secondary,
              }}
            >
              {t("QUOTES_PAYMENT_FREQUENCY.monthly")}
            </Typography>
          )}
        </Box>
        <Box>{InsurerLogoMapping[plan.product_details.insurer]}</Box>
      </Box>
    </Box>
  );
};

const ProductQuoteCard = ({
  prodIcon,
  prodTitle,
  plans,
}: {
  prodIcon: any;
  prodTitle: string;
  plans: Quote[];
}) => {
  const customStyles = useSelector(customStylesSelector);
  const { selectedQuotes, status, polling } = useSelector(quotesSelector);
  const { invalidQuotes, is_add_on, visiblePlanNames } = useSelector(
    (state: RootState) => state.quotes
  );
  const productIdentifier = useSelector(
    (state: RootState) =>
      state.client.availableProducts[0]?.product?.product_identifier
  );
  const carrier = useSelector(
    (state: RootState) => state.client.availableProducts[0]?.carrier?.[0]
  );
  const isSilverySky =
    COUNTRY === "sg" && productIdentifier === "PET" && carrier === "MSIG";
  const dispatch = useDispatch();
  const { t } = useT();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [lastSelectedPlanName, setLastSelectedPlanName] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (selectedQuotes.length > 0 && isSilverySky) {
      const planName =
        selectedQuotes[0]?.premium?.additional_info?.plan_name?.default;
      if (planName) {
        setLastSelectedPlanName(planName);
      }
    }
  }, [selectedQuotes, isSilverySky]);

  const getSelectedQuote = () => {
    return plans.find((p) => selectedQuotes?.find((sp) => sp.id === p.id));
  };
  // Check if there are any invalid quotes for this product type
  const hasInvalidQuotesForProduct = invalidQuotes.some(
    (quote) =>
      quote.product_details?.type?.toLowerCase() === prodTitle?.toLowerCase()
  );

  useEffect(() => {
    if (
      !selectedQuotes.some(
        (sq) =>
          sq.product_details?.type?.toLowerCase() ===
          plans[0]?.product_details?.type?.toLowerCase()
      )
    ) {
      const monthlyPlans = plans.filter((q) => q?.premium?.monthly);
      if (monthlyPlans.length) {
        dispatch(addOrRemoveQuote(monthlyPlans[0]));
      } else if (plans[0]) {
        dispatch(addOrRemoveQuote(plans[0]));
      }
    }
  }, []);

  // Show loading state if polling and no quotes (valid or invalid) for this product
  if (polling && !plans.length && !hasInvalidQuotesForProduct) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          mx: "-16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            minHeight: "116px",
            padding: "20px",
            borderRadius: "12px",
            backgroundColor: customStyles.primaryCard.backgroundColor,
            mt: "20px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              sx={{
                padding: "10px",
                borderRadius: "12px",
                backgroundColor: customStyles.secondaryCard.backgroundColor,
              }}
            >
              <Box sx={{ width: "40px", height: "40px" }}>{prodIcon}</Box>
            </Box>

            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                color: customStyles.muiTheme.palette.text.secondary,
              }}
            >
              {prodTitle}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              color: customStyles.muiTheme.palette.text.secondary,
            }}
          >
            <CircularProgress
              size={20}
              sx={{
                color: "inherit",
              }}
            />
            <Typography>{t("FETCHING_QUOTES")}...</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!plans?.length) {
    return (
      <QuotesMissing
        invalidQuote={invalidQuotes.find(
          (quote) =>
            quote.product_details?.type?.toLowerCase() ===
            prodTitle?.toLowerCase()
        )}
      />
    );
  }

  const Plans = (
    <Box
      sx={{
        display: "flex",
        gap: "20px",
        flexWrap: "wrap",
        justifyContent: isMobile ? "center" : "end",
      }}
    >
      {isSilverySky
        ? plans
            .filter((plan) => {
              // If there are selected quotes, filter based on them
              if (selectedQuotes.length > 0) {
                return selectedQuotes.some(
                  (sq) =>
                    sq.premium?.additional_info?.plan_name?.default ===
                    plan.premium?.additional_info?.plan_name?.default
                );
              } else if (lastSelectedPlanName) {
                return (
                  plan.premium?.additional_info?.plan_name?.default ===
                  lastSelectedPlanName
                );
              }
              return plan.id === plans[0]?.id;
            })
            .map((plan) => <PlanPriceCard plan={plan} key={plan?.id} />)
        : plans.map((plan) => <PlanPriceCard plan={plan} key={plan?.id} />)}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        mx: "-16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          minHeight: "116px",
          padding: "20px",
          borderRadius: "12px",
          backgroundColor: customStyles.primaryCard.backgroundColor,
          mt: "20px",
        }}
      >
        {/* prod details */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              sx={{
                padding: "10px",
                borderRadius: "12px",
                backgroundColor: customStyles.secondaryCard.backgroundColor,
              }}
            >
              <Box sx={{ width: "40px", height: "40px" }}>{prodIcon}</Box>
            </Box>

            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                color: customStyles.muiTheme.palette.text.secondary,
              }}
            >
              {prodTitle}
            </Typography>
          </Box>

          {/* plans on desktop */}
          {!isMobile && Plans}
        </Box>
        {/* plans on mobile */}
        {isMobile && Plans}
      </Box>

      {/* coverage details of the selected plan */}
      <Coverages selectedQuote={getSelectedQuote()} showHeading={true} />

      {/* add-on coverage details */}
      {isSilverySky && is_add_on && selectedQuotes.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
            borderRadius: "12px",
            backgroundColor: customStyles.primaryCard.backgroundColor,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              color: customStyles.muiTheme.palette.text.secondary,
            }}
          >
            Non-Surgical Cover
          </Typography>

          <Coverages
            selectedQuote={{
              ...selectedQuotes.find((q) => q.add_on_coverage),
              coverage: selectedQuotes.find((q) => q.add_on_coverage)
                ?.add_on_coverage,
            }}
            showHeading={false}
          />
        </Box>
      )}

      {/* Summary section would be here */}
    </Box>
  );
};

export default ProductQuoteCard;
